// @owners { team: patients }

// eslint-disable-next-line import/no-deprecated
import { saveOrder as saveOrderAction, savingOrder } from '~shared/actions/checkout';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useOrderPricing } from '~shared/queries';
import { useDispatchShared } from '~shared/store';
import { type APIError } from '~shared/types/APIError';
import { useAchPreflightCheck } from './useAchPreflightCheck';
import { useHandleCreateOrderError } from './useHandleCreateOrderError';
import { useHandleCreateOrderSuccess } from './useHandleCreateOrderSuccess';

type SaveOrderSuccessResponse = { shipment_id: number | null; split_shipment_ids: number[] | null };
type SaveOrderErrorResponse = { error: APIError };
const isSaveOrderError = (
  response: SaveOrderSuccessResponse | SaveOrderErrorResponse,
): response is SaveOrderErrorResponse => 'error' in response;

type UseSaveOrderParams = {
  enableAchPreflight?: boolean;
};

export const useSaveOrder = ({ enableAchPreflight = false }: UseSaveOrderParams = {}) => {
  const dispatch = useDispatchShared();
  const { handleCreateOrderSuccess } = useHandleCreateOrderSuccess();
  const { handleCreateOrderError } = useHandleCreateOrderError();
  const { appliedShipmentFees, orderPricing } = useOrderPricing();
  const { selectedPaymentTypes } = useCartData();
  const { runPreflightCheck } = useAchPreflightCheck();

  const saveOrderApiCall = async () => {
    dispatch(savingOrder());

    if (enableAchPreflight) {
      try {
        await runPreflightCheck();
      } catch (err) {
        const apiError = err as APIError;
        handleCreateOrderError(apiError);
        return { error: apiError };
      }
    }

    // eslint-disable-next-line import/no-deprecated
    const response = await dispatch(saveOrderAction(appliedShipmentFees, orderPricing, selectedPaymentTypes));

    if (isSaveOrderError(response)) {
      handleCreateOrderError(response.error);
      return response;
    }

    const shipmentIds = response.shipment_id !== null ? [response.shipment_id] : response.split_shipment_ids;

    handleCreateOrderSuccess({ shipmentIds });

    return response as SaveOrderSuccessResponse;
  };

  return { saveOrderApiCall };
};
